/**
 * `<Card />` — Story 5.4 (UX §4.1 line 881, AC line 1008).
 *
 * Generic surface container with four variants. Layout-only — Card
 * does NOT render images, captions, or actions itself; the consumer
 * composes via `children` + the named `header` / `footer` slots.
 *
 * Variants:
 *   - `photo`      — voter gallery + admin queue + reveal display.
 *                    4:3 aspect-preserved frame; no shadow (the photo
 *                    is the visual interest).
 *   - `queue-item` — admin moderation queue (Story 7.5). Compact
 *                    horizontal layout; `--elev-1` shadow.
 *   - `metric`     — admin dashboard tile (Story 7.2). Big-number on
 *                    top + small-label below; `--elev-1` shadow.
 *   - `reveal`     — broadcast reveal display (Story 12.7). Full-canvas
 *                    placement; no padding from the card itself; no
 *                    shadow per UX §2.5 broadcast rules.
 *
 * Defaults:
 *   - Border-radius `var(--radius-lg)` = 14px (AC line 1008).
 *   - Background `var(--bg-surface-raised)`.
 */
import type { HTMLAttributes, ReactNode } from 'react';

export type CardVariant = 'photo' | 'queue-item' | 'metric' | 'reveal';

export interface CardProps extends HTMLAttributes<HTMLDivElement> {
  variant: CardVariant;
  header?: ReactNode;
  footer?: ReactNode;
  children?: ReactNode;
}

function variantStyle(variant: CardVariant): {
  padding: string;
  boxShadow: string;
  aspectRatio?: string;
  display?: string;
  flexDirection?: 'row' | 'column';
} {
  switch (variant) {
    case 'photo':
      return {
        padding: '0',
        boxShadow: 'none',
        aspectRatio: '4 / 3',
        display: 'flex',
        flexDirection: 'column',
      };
    case 'queue-item':
      return {
        padding: 'var(--space-3)',
        boxShadow: 'var(--elev-1)',
        display: 'flex',
        flexDirection: 'row',
      };
    case 'metric':
      return {
        padding: 'var(--space-4)',
        boxShadow: 'var(--elev-1)',
        display: 'flex',
        flexDirection: 'column',
      };
    case 'reveal':
      return {
        padding: '0',
        boxShadow: 'none',
        display: 'flex',
        flexDirection: 'column',
      };
  }
}

/**
 * `Card` — variant-themed surface container.
 */
export function Card({
  variant,
  header,
  footer,
  children,
  style,
  ...rest
}: CardProps) {
  const v = variantStyle(variant);
  return (
    <div
      {...rest}
      data-variant={variant}
      style={{
        background: 'var(--bg-surface-raised)',
        color: 'var(--text-primary)',
        borderRadius: 'var(--radius-lg)',
        padding: v.padding,
        boxShadow: v.boxShadow,
        aspectRatio: v.aspectRatio,
        display: v.display,
        flexDirection: v.flexDirection,
        ...style,
      }}
    >
      {header && <div data-testid="card-header">{header}</div>}
      {children}
      {footer && <div data-testid="card-footer">{footer}</div>}
    </div>
  );
}
